import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Store, Camera, FileText, MessageCircle } from 'lucide-react';

/**
 * Persistent mini-menu that sticks to the bottom of the viewport on public
 * pages (Store home, Portfolio, Get-Quote). Highlights the current route so
 * the user never loses their place.
 *
 * Usage: <StickyFooterNav /> — no props needed. Safely no-ops on admin routes
 * because those pages don't import it.
 */
export default function StickyFooterNav({ whatsappNumber = '917418086026' }) {
  const { pathname } = useLocation();
  const is = (p) => (p === '/' ? pathname === '/' : pathname.startsWith(p));

  const items = [
    { key: 'shop', to: '/', label: 'Shop', icon: Store, active: is('/') && !is('/portfolio') && !is('/get-quote'), testId: 'sticky-nav-shop' },
    { key: 'portfolio', to: '/portfolio', label: 'Portfolio', icon: Camera, active: is('/portfolio'), testId: 'sticky-nav-portfolio' },
    { key: 'quote', to: '/get-quote', label: 'Get Quote', icon: FileText, active: is('/get-quote'), testId: 'sticky-nav-quote' },
  ];

  const waNum = (whatsappNumber || '').replace(/[^\d]/g, '') || '917418086026';

  return (
    <>
      {/* Spacer so page content isn't hidden behind the sticky bar */}
      <div aria-hidden className="h-16" />
      <nav
        data-testid="sticky-footer-nav"
        className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-stone-200 shadow-[0_-2px_12px_rgba(0,0,0,0.04)]"
        aria-label="Primary navigation"
      >
        <div className="max-w-3xl mx-auto grid grid-cols-4">
          {items.map(({ key, to, label, icon: Icon, active, testId }) => (
            <Link
              key={key}
              to={to}
              data-testid={testId}
              className={`flex flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] transition ${active ? 'text-amber-800 font-semibold' : 'text-stone-500 hover:text-stone-800'}`}
            >
              <Icon className="w-5 h-5" strokeWidth={active ? 2.25 : 1.75} />
              {label}
              {active && <span className="w-1 h-1 rounded-full bg-amber-700 -mt-0.5" aria-hidden />}
            </Link>
          ))}
          <a
            data-testid="sticky-nav-whatsapp"
            href={`https://wa.me/${waNum}`}
            target="_blank"
            rel="noreferrer"
            className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] text-lime-700 hover:text-lime-800 transition"
          >
            <MessageCircle className="w-5 h-5" strokeWidth={1.75} />
            WhatsApp
          </a>
        </div>
      </nav>
    </>
  );
}
